import React, { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import isEqual from 'lodash/isEqual'
import moment from 'moment'

import { parseISO, subHours } from 'date-fns'

import LoadingContext from '../../context/Loading/Loading'
import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'
import PackageDetailsContext from '../../context/PackageDetails/PackageDetails'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import {
  checkPermission,
  getTimezoneDate,
  b64toBlob,
  convertBlobToDataURL,
  generateRandomColor,
  getFormattedTimezoneDate,
  toUtcIsoFromTimezone,
  scrollToTop,
} from '../../utilities/helpers'

import useSafeAsync from '../../utilities/useSafeAsync'

import {
  getPackage,
  updatePackageDetails,
  upload,
  getVenueEvents,
  getCategories,
  getSeasons,
} from '../../utilities/api'

import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import Alert from 'react-bootstrap/Alert'

import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'

import { Details } from './Details'
import { EventsList } from './EventsList'
import { CreatePackageButtons } from '../CreatePackageButtons'
import { DateTime } from './DateTime'
import { Inventory } from './Inventory'
import { ServiceFee } from './ServiceFee'
import PackageCustomFee from './PackageCustomFee/PackageCustomFee'

// A package can carry a name before it has a schedule, so a missing or
// unparseable date has to fall back to the default rather than reach the date
// pickers as an invalid Date.
const toPackageDate = (value, timezone, fallback) => {
  if (!value) return fallback
  const parsed = new Date(getFormattedTimezoneDate(value, timezone))
  return Number.isNaN(parsed.getTime()) ? fallback : parsed
}

export default function PackageDetailsWrapper({ packageId }) {
  const safeSetState = useSafeAsync()

  const {
    eventPackage: packageFromContext,
    updatePackage,
    storePackageStart,
    storePackageEnd,
    updateCanPublish,
    updateIsPackagePublished,
    updateIsPackageOnsale,
    updateIsPackageSoldout,
    hasPackageEnded,
    updateHasPackageEnded,
    isPackagePublished,
    updateHasLastEventInPackageEnded,
  } = useContext(PackageDetailsContext)

  const navigate = useNavigate()

  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const { orgPermissions } = useContext(UserContext)

  const organization = AuthService.getOrg()[0]
  const isSuperAdmin = AuthService.isSuperAdminOrganizer()

  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.SEASON_PACKAGES_MANAGEMENT
    )

  const [initialState, setInitialState] = useState()

  const [showFooter, setShowFooter] = useState(false)

  const [seasons, setSeasons] = useState()

  const [allEvents, setAllEvents] = useState([])

  const [isLoadingEvents, setIsLoadingEvents] = useState(false)

  const [categories, setCategories] = useState([])

  const [selectedEventIds, setSelectedEventIds] = useState([])

  const [eventPackage, setEventPackage] = useState()

  const [inventory, setInventory] = useState()

  const [pricingTiers, setPricingTiers] = useState({})

  const [isSaving, setIsSaving] = useState(false)

  const [start, setStart] = useState(
    new Date(moment('7:00 pm', 'h:mm a').format())
  )

  const [end, setEnd] = useState(
    new Date(moment('11:00 pm', 'h:mm a').format())
  )

  const [endOfLastEvent, setEndOfLastEvent] = useState(
    new Date(moment('11:00 pm', 'h:mm a').format())
  )

  const [image, setImage] = useState()

  const [coordinates, setCoordinates] = useState()

  const [isEditing, setIsEditing] = useState(false)

  const [alert, setAlert] = useState({
    show: false,
    variant: '',
    message: '',
  })

  const [hasRequiredFieldError, setHasRequiredFieldError] = useState(false)

  // required fields error status
  const [requiredFieldErrorStatus, setRequiredFieldErrorStatus] = useState({})

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }

    showLoading()
    loadPackage()
  }, [orgPermissionsReady, hasPermission, packageId])

  // show/hide footer
  useEffect(() => {
    console.log(
      'show footer all',
      Object.values(initialState?.pricingTiers ?? {}),
      setShowFooter(
        !isEqual(initialState?.eventPackage, eventPackage) ||
          !isEqual(initialState?.image, image) ||
          !isEqual(new Date(initialState?.start), new Date(start)) ||
          !isEqual(initialState?.selectedEventIds, selectedEventIds) ||
          !isEqual(initialState?.pricingTiers, pricingTiers)
      )
    )
  }, [eventPackage, initialState, image, start, selectedEventIds, pricingTiers])

  useEffect(() => {
    // close all alerts when fields are edited and there is no required fields error
    if (alert.show && !hasRequiredFieldError) {
      closeAlert()
    }
  }, [eventPackage, image, start, selectedEventIds])

  useEffect(() => {
    // set end date to 24 hours before the first event starts
    // take selected event id that matches first event

    const firstEvent = sort(allEvents)?.find((event) =>
      selectedEventIds.includes(event.id)
    )

    if (!firstEvent) return

    if (selectedEventIds?.length > 0) {
      // Parse the UTC string
      const parsedDate = parseISO(firstEvent?.start)

      // Subtract 24 hours
      const endDateObj = subHours(parsedDate, 24)

      // Convert date back to ISO string in UTC format
      setEnd(endDateObj?.toISOString())

      // take selected event id that matches last event
      // reverse allEvents without mutating original array
      const lastEvent = [...sort(allEvents)]
        .reverse()
        ?.find((event) => selectedEventIds.includes(event.id))
      console.log('lastevent ends UTC', lastEvent?.end)
      setEndOfLastEvent(lastEvent?.end)
    }
  }, [allEvents, selectedEventIds])

  useEffect(() => {
    if (hasRequiredFieldError) {
      updateRequiredFieldErrorStatus()
    }
  }, [
    eventPackage?.name,
    eventPackage?.category,
    pricingTiers,
    eventPackage?.season,
    image,
    selectedEventIds,
  ])

  useEffect(() => {
    if (hasRequiredFieldError) {
      // if all fields are filled in: reset required field state
      if (
        Object.keys(requiredFieldErrorStatus).every(
          (key) => !requiredFieldErrorStatus[key]
        )
      ) {
        setHasRequiredFieldError(false)
        setRequiredFieldErrorStatus({})
      }
    }
  }, [hasRequiredFieldError, requiredFieldErrorStatus])

  useEffect(() => {
    if (hasRequiredFieldError) {
      // if all fields are filled in: dismiss alert
      if (
        Object.keys(requiredFieldErrorStatus).every(
          (key) => !requiredFieldErrorStatus[key]
        )
      ) {
        closeAlert()
        setHasRequiredFieldError(false)
        setRequiredFieldErrorStatus({})
      }
    }
  }, [hasRequiredFieldError, requiredFieldErrorStatus])

  const updateRequiredFieldErrorStatus = () => {
    console.log(
      'update required pricie',
      Object.keys(pricingTiers)?.length === 0
    )
    setRequiredFieldErrorStatus({
      season: !eventPackage?.season,
      name: !eventPackage?.name,
      category: !eventPackage?.category,
      image: !image,
      pricingTiers: Object.keys(pricingTiers)?.length === 0,
      events: selectedEventIds?.length === 0,
    })
  }

  const closeAlert = () => {
    setAlert({ show: false, variant: '', message: '' })
  }

  // sort events by the earliest start date in ascending order
  const sort = (events) => {
    return events.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
  }

  const loadSeasons = async () => {
    try {
      const res = await getSeasons(organization.uuid)
      safeSetState(() => {
        setSeasons(res?.data)
      })
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  const loadVenueEvents = async (roomUUID, seatmapUUID, organizationUUID) => {
    try {
      safeSetState(() => {
        setIsLoadingEvents(true)
      })

      const res = await getVenueEvents(roomUUID, seatmapUUID, organizationUUID)

      safeSetState(() => {
        const sortedEvents = sort(res?.data)
        setAllEvents(sortedEvents)
      })
    } catch (err) {
      console.error(err)
      throw err // Re-throw the error if you need error handling in the calling function
    } finally {
      safeSetState(() => {
        setIsLoadingEvents(false)
      })
    }
  }

  const loadCategories = async () => {
    try {
      const res = await getCategories()
      safeSetState(() => {
        setCategories(res?.data)
      })
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  const loadPackage = async () => {
    try {
      const res = await getPackage(packageId)
      if (!res?.data) return

      safeSetState(() => {
        setEventPackage(res?.data)
      })

      // Use active session org (same as seasons) so switched-org superAdmin sees events.
      const orgUUID =
        organization?.uuid ||
        res?.data?.organization_uuid ||
        res?.data?.organizationUUID ||
        res?.data?.organization?.uuid

      await Promise.all([
        loadVenueEvents(
          res?.data?.room_uuid || res?.data?.room?.uuid,
          res?.data?.seatmap?.id,
          orgUUID
        ),
        loadCategories(),
        loadSeasons(),
      ])

      await new Promise((resolve) => {
        safeSetState(() => {
          setIsEditing(
            Boolean(res?.data?.name) ||
              Boolean(res?.data?.package_tickets?.length > 0)
          )

          let inventory = { color: generateRandomColor() }

          if (res?.data?.package_tickets) {
            inventory = { ...inventory, seats: res?.data?.package_tickets }
          } else {
            inventory = { ...inventory, seats: [] }
          }

          let tiers = {}
          if (res.data?.pricingTiers) {
            // re-key the pricingTiers array from API
            const obj = res.data?.pricingTiers.reduce((acc, item) => {
              acc[item.id] = item
              return acc
            }, {})
            tiers = obj
            setPricingTiers(obj)
          }

          if (res?.data?.name) {
            setImage(res?.data?.image?.url)

            const formattedStart = toPackageDate(
              res?.data?.start,
              res?.data?.timezone,
              start
            )
            setStart(formattedStart)

            const formattedEnd = toPackageDate(
              res?.data?.end,
              res?.data?.timezone,
              end
            )
            setEnd(formattedEnd)

            const eventIds = sort(res?.data?.events)?.map((event) => event.id)
            setSelectedEventIds(eventIds)

            inventory = { ...inventory, color: res?.data?.color }

            saveInitialState(
              res?.data,
              formattedStart,
              formattedEnd,
              res?.data?.image?.url,
              eventIds,
              tiers
            )
          } else {
            saveInitialState(
              res?.data,
              start,
              end,
              image,
              selectedEventIds,
              tiers
            )
          }

          setInventory(inventory)

          // Update context
          updatePackage(res?.data)
          updateIsPackageOnsale(res?.data)
          updateHasPackageEnded(res.data)
          updateIsPackageSoldout(res.data)
          updateIsPackagePublished(res?.data)

          resolve() // Wait for this block to complete
        })
      })
    } catch (err) {
      console.error(err)
    } finally {
      hideLoading()
    }
  }

  const saveInitialState = (
    eventPackage,
    start,
    end,
    image,
    selectedEventIds,
    pricingTiers
  ) => {
    // save initial state to check whether to show save buttons
    setInitialState({
      eventPackage,
      image,
      start,
      end,
      selectedEventIds,
      pricingTiers,
    })
  }

  const handleChange = (e) => {
    if (e?.target)
      setEventPackage({ ...eventPackage, [e.target.name]: e.target.value })
  }

  const handleCheckboxChange = (e) => {
    if (!e?.target) return
    const { name, checked } = e.target
    setEventPackage({ ...eventPackage, [name]: checked })
  }

  const handleCustomFees = (customFees) => {
    setEventPackage({ ...eventPackage, customFees })
  }

  const handleSeason = (e) => {
    const id = Number(e.target.value)
    const season = seasons.find((season) => season.id === id)
    setEventPackage({ ...eventPackage, season })
  }

  const updateDetails = (data) => {
    updatePackageDetails(data)
      .then((res) => {
        // save initial state again if editing to check whether to show save buttons
        saveInitialState(
          eventPackage,
          start,
          end,
          image,
          selectedEventIds,
          pricingTiers
        )
        scrollToTop()
        setAlert({
          show: true,
          variant: 'success',
          message: 'Your details have been updated.',
        })

        // Details save does not reload seat inventory. Keep the seats and
        // venue already on the package so the map still has them.
        const savedEventIds = (res?.data?.events || [])
          .map((event) => event.id)
          .join(',')
        const previousEventIds = (packageFromContext?.events || [])
          .map((event) => event.id)
          .join(',')
        const savedPackage = {
          ...(packageFromContext || {}),
          ...(res?.data || {}),
          events:
            res?.data?.events && savedEventIds !== previousEventIds
              ? res.data.events
              : (packageFromContext?.events ?? res?.data?.events),
          package_tickets:
            res?.data?.package_tickets ?? packageFromContext?.package_tickets,
          seatmap: res?.data?.seatmap ?? packageFromContext?.seatmap,
          organization:
            res?.data?.organization ?? packageFromContext?.organization,
          room: res?.data?.room ?? packageFromContext?.room,
          venue: res?.data?.venue
            ? { ...(packageFromContext?.venue || {}), ...res.data.venue }
            : packageFromContext?.venue,
        }
        updatePackage(savedPackage)
        updateCanPublish(savedPackage?.name, savedPackage?.package_tickets)
        storePackageStart(res?.data?.start)
        storePackageEnd(res?.data?.end)
        updateIsPackageOnsale(res?.data)
        updateHasPackageEnded(res?.data)
        updateIsPackageSoldout(res?.data)
        updateIsPackagePublished(res?.data)
        updateHasLastEventInPackageEnded(
          res?.data?.endOfLastEvent,
          res?.data?.timezone
        )

        setIsSaving(false)

        if (!isEditing) {
          navigate(`/package/${eventPackage?.uuid}/seatmap`)
        }
      })
      .catch((err) => {
        console.error(err)
        setIsSaving(false)
        scrollToTop()
        setAlert({
          show: true,
          variant: 'danger',
          message: 'Unable to save info. Please try again.',
        })
      })
  }

  const handleSave = (e) => {
    e.preventDefault()
    // if error, don't save
    if (hasRequiredFieldError) {
      scrollToTop()
      return
    }

    const hasError =
      !eventPackage?.season ||
      !eventPackage?.name ||
      !eventPackage?.category ||
      !image ||
      Object.keys(pricingTiers)?.length === 0 ||
      selectedEventIds?.length === 0

    if (hasError) {
      updateRequiredFieldErrorStatus()
      setHasRequiredFieldError(hasError)
      scrollToTop()
      setAlert({
        show: true,
        variant: 'danger',
        message:
          'You are missing required subject fields. Please fill out all required fields before continuing.',
      })
      return
    }

    setIsSaving(true)
    const data = {}
    data['id'] = eventPackage.id
    data['season'] = Number(eventPackage.season.id)
    data['name'] = eventPackage.name
    data['category'] = eventPackage.category
    data['description'] = eventPackage.description
    data['invoiceEmailNote'] = eventPackage.invoiceEmailNote || ''
    data['events'] = selectedEventIds
    data['start'] = toUtcIsoFromTimezone(start, eventPackage?.timezone)
    data['end'] = end
    data['endOfLastEvent'] = endOfLastEvent
    // Ticket limits UI removed; clear so purchase is not constrained by stale values.
    data['minQuantity'] = null
    data['maxQuantity'] = null
    data['useFlatServiceFee'] = Boolean(eventPackage?.useFlatServiceFee)
    data['flatServiceFee'] = eventPackage?.useFlatServiceFee
      ? eventPackage?.flatServiceFee === '' ||
        eventPackage?.flatServiceFee === null ||
        eventPackage?.flatServiceFee === undefined
        ? 0
        : eventPackage.flatServiceFee
      : null
    data['customFees'] = (eventPackage?.customFees || [])
      .map((fee) => fee?.id)
      .filter((id) => id != null && id !== '')
    data['pricingTiers'] = Object.values(pricingTiers)
    data['color'] = inventory?.color
    data['orgUUID'] = organization?.uuid

    // add image to package
    if (image && !image.startsWith('https://')) {
      // new or updating image
      convertBlobToDataURL(image).then((dataUrl) => {
        const formData = new FormData()
        const ImageURL = dataUrl
        // Split the base64 string in data and contentType
        const block = ImageURL.split(';')
        // Get the content type of the image
        const contentType = block[0].split(':')[1] // In this case "image/gif"
        // get the real base64 content of the file
        const realData = block[1].split(',')[1] // In this case "R0lGODlhPQBEAPeoAJosM...."

        // Convert it to a blob to upload
        const blob = b64toBlob(realData, contentType)
        formData.append(`files`, blob)
        upload(formData).then((res) => {
          data['image'] = res?.data[0]?.id
          updateDetails(data)
        })
      })
      // update with existing image
    } else {
      data['image'] = eventPackage?.image?.id // take existing image
      updateDetails(data)
    }
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  return (
    <>
      <div className="position-relative">
        <section
          className="wrapper package-form"
          data-testid="package-details-wrapper-section"
        >
          {alert.show && (
            <>
              <Alert variant={alert.variant} className="mb-5">
                <p>{alert.message}</p>
              </Alert>
            </>
          )}
          <header className="section-header section-heading section-heading--secondary">
            <h1>Package details</h1>
          </header>
          <Form onSubmit={handleSave}>
            <Card body className="card--md card--light">
              <Details
                eventPackage={eventPackage}
                seasons={seasons}
                categories={categories}
                image={image}
                setImage={setImage}
                coordinates={coordinates}
                setCoordinates={setCoordinates}
                handleChange={handleChange}
                handleSeason={handleSeason}
                requiredFieldErrorStatus={requiredFieldErrorStatus}
                isPackagePublished={isPackagePublished}
                isEditable={hasPackageEnded}
              />
            </Card>
            <Card body className="card--md card--light">
              <DateTime
                start={start}
                setStart={setStart}
                isEditable={hasPackageEnded}
              />
            </Card>
            <Card body className="card--md card--light">
              <Inventory
                eventPackage={eventPackage}
                inventory={inventory}
                pricingTiers={pricingTiers}
                setPricingTiers={setPricingTiers}
                requiredFieldErrorStatus={requiredFieldErrorStatus}
                isDisabled={hasPackageEnded}
              />
            </Card>
            <Card body className="card--md card--light" id="events-list">
              <EventsList
                allEvents={sort(allEvents)}
                timezone={eventPackage?.timezone}
                selectedEventIds={selectedEventIds}
                setSelectedEventIds={setSelectedEventIds}
                requiredFieldErrorStatus={requiredFieldErrorStatus}
                isLoading={isLoadingEvents}
                isDisabled={isPackagePublished || hasPackageEnded}
              />
            </Card>
            {isSuperAdmin && (
              <Card body className="card--md card--light">
                <ServiceFee
                  eventPackage={eventPackage}
                  handleChange={handleChange}
                  handleCheckboxChange={handleCheckboxChange}
                  isEditable={hasPackageEnded}
                />
              </Card>
            )}
            <Card body className="card--md card--light">
              <PackageCustomFee
                eventPackage={eventPackage}
                organizationUUID={organization?.uuid}
                onSelect={handleCustomFees}
                isDisabled={hasPackageEnded}
              />
            </Card>
          </Form>
        </section>

        {showFooter && (
          <CreatePackageButtons
            isEditing={isEditing}
            isSaving={isSaving}
            showGoBack={!isEditing}
            styles=""
            handleSave={handleSave}
            handleGoBack={handleGoBack}
          />
        )}
      </div>
    </>
  )
}
